import React, { useContext, useState } from 'react';
import {
  Box, Chip, Dialog, DialogTitle, DialogContent,
  Divider, IconButton, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { storeContext } from '../provider/Provider';
import AiRecommendations from './AiRecommendations';

const useStyles = makeStyles(() => ({
  card: {
    padding: 24,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: 20,
  },
  sectionHeading: {
    fontWeight: 700,
    fontSize: 20,
    color: '#58595B',
    marginBottom: 4,
  },
  sectionDesc: {
    fontSize: 13,
    color: '#999',
    marginBottom: 16,
  },
  colTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#58595B',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 12,
  },
  platformRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
  },
  platformIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    marginRight: 8,
    flexShrink: 0,
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
  },
  tag: {
    margin: '0 6px 6px 0',
    fontWeight: 600,
    fontSize: 11,
    borderRadius: 20,
  },
  compRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #f3f4f6',
    minHeight: 52,
  },
  compLabel: {
    width: 160,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: 13,
    color: '#58595B',
    paddingLeft: 8,
  },
  compCell: {
    flex: 1,
    textAlign: 'center',
  },
  compHeader: {
    fontWeight: 700,
    fontSize: 13,
    color: '#333',
  },
}));

/* ── SVG Gauges ─────────────────────────────────────────────────────────── */

function PercentileRing({ percentile, size = 110 }) {
  const strokeWidth = 8;
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const filled = ((100 - percentile) / 100) * circ;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#0d9373" strokeWidth={strokeWidth}
          strokeDasharray={`${filled} ${circ - filled}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0d9373', lineHeight: 1 }}>TOP</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: '#0d9373', lineHeight: 1.1 }}>
          {percentile}%
        </span>
      </div>
    </div>
  );
}

PercentileRing.propTypes = { percentile: PropTypes.number.isRequired, size: PropTypes.number };
PercentileRing.defaultProps = { size: 110 };

function SemiGauge({ score, size = 130 }) {
  const sw = 10;
  const r = (size - sw * 2) / 2;
  const arcLen = Math.PI * r;
  const filled = (Math.min(score, 100) / 100) * arcLen;
  const cy = size / 2 + 8;

  let color = '#ef4444';
  let label = 'Low';
  if (score >= 70) { color = '#10b981'; label = 'Great'; }
  else if (score >= 50) { color = '#0d9373'; label = 'Good'; }
  else if (score >= 30) { color = '#f59e0b'; label = 'Medium'; }

  return (
    <svg width={size} height={size / 2 + 24} style={{ display: 'block', margin: '0 auto' }}>
      <path
        d={`M ${sw},${cy} A ${r},${r} 0 0,1 ${size - sw},${cy}`}
        fill="none" stroke="#e5e7eb" strokeWidth={sw} strokeLinecap="round"
      />
      <path
        d={`M ${sw},${cy} A ${r},${r} 0 0,1 ${size - sw},${cy}`}
        fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round"
        strokeDasharray={`${filled} ${arcLen}`}
      />
      <text x={size / 2} y={cy - 6} textAnchor="middle" fontSize={30} fontWeight="700" fill="#333">
        {score}
      </text>
      <text x={size / 2} y={cy + 14} textAnchor="middle" fontSize={12} fontWeight="600" fill={color}>
        {label}
      </text>
    </svg>
  );
}

SemiGauge.propTypes = { score: PropTypes.number.isRequired, size: PropTypes.number };
SemiGauge.defaultProps = { size: 130 };

/* ── Platform config ────────────────────────────────────────────────────── */

const PLATFORMS = [
  { key: 'chatgpt', name: 'ChatGPT', letter: 'C', bg: '#10b981' },
  { key: 'google_ai_overview', name: 'Google AI Overview', letter: 'G', bg: '#4285f4' },
  { key: 'gemini', name: 'Gemini', letter: 'G', bg: '#10b981' },
  { key: 'perplexity', name: 'Perplexity', letter: 'P', bg: '#7c3aed' },
  { key: 'claude', name: 'Claude', letter: 'C', bg: '#d4a574' },
];

function formatCount(n) {
  if (n === null || n === undefined) return null;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ── Main component ─────────────────────────────────────────────────────── */

function InsightsSkeleton({ classes }) {
  return (
    <>
      <Typography className={classes.sectionHeading}>AI Insights</Typography>
      <Typography className={classes.sectionDesc}>
        Loading visibility data...
      </Typography>
      <Paper className={classes.card}>
        <Box display="flex" flexWrap="wrap" style={{ gap: 0 }}>
          <Box flex={1} minWidth={200} pr={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Skeleton variant="text" width={140} height={20} style={{ marginBottom: 12 }} />
            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              <Skeleton variant="circle" width={110} height={110} />
              <Box>
                <Skeleton variant="text" width={140} />
                <Skeleton variant="text" width={120} />
              </Box>
            </Box>
          </Box>
          <Box flex={1} minWidth={180} px={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 12 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} display="flex" justifyContent="space-between" mb={1}>
                <Skeleton variant="text" width={130} />
                <Skeleton variant="text" width={50} />
              </Box>
            ))}
          </Box>
          <Box flex={1} minWidth={180} px={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Skeleton variant="text" width={120} height={20} style={{ marginBottom: 12 }} />
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i} display="flex" justifyContent="space-between" mb={1}>
                <Skeleton variant="text" width={140} />
                <Skeleton variant="text" width={40} />
              </Box>
            ))}
          </Box>
          <Box flex={1} minWidth={200} pl={3}>
            <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 12 }} />
            <Box display="flex" style={{ gap: 6 }} mb={2}>
              <Skeleton variant="rect" width={100} height={28} style={{ borderRadius: 20 }} />
              <Skeleton variant="rect" width={120} height={28} style={{ borderRadius: 20 }} />
            </Box>
            <Skeleton variant="rect" width="100%" height={22} style={{ borderRadius: 4, marginBottom: 8 }} />
            <Skeleton variant="rect" width="80%" height={22} style={{ borderRadius: 4 }} />
          </Box>
        </Box>
      </Paper>

      <Skeleton variant="text" width={280} height={28} style={{ marginTop: 8, marginBottom: 4 }} />
      <Skeleton variant="text" width={400} height={16} style={{ marginBottom: 16 }} />
      <Paper className={classes.card}>
        <Box display="flex" justifyContent="space-around" py={2}>
          {[1, 2, 3].map((i) => (
            <Box key={i} textAlign="center">
              <Skeleton variant="circle" width={130} height={65} style={{ margin: '0 auto 8px', borderRadius: 65 }} />
              <Skeleton variant="text" width={80} style={{ margin: '0 auto' }} />
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
}

InsightsSkeleton.propTypes = {
  classes: PropTypes.shape({
    sectionHeading: PropTypes.string,
    sectionDesc: PropTypes.string,
    card: PropTypes.string,
  }).isRequired,
};

const AiInsights = ({ insights, loading }) => {
  const classes = useStyles();
  const { state } = useContext(storeContext);
  const domain = state.websiteSwitch.currentWebsite;

  const [mentionsModal, setMentionsModal] = useState(false);
  const [locationsModal, setLocationsModal] = useState(false);

  if (loading && !insights) return <InsightsSkeleton classes={classes} />;
  if (!insights) return null;

  const {
    visibility_score: visScore,
    top_percentile: percentile,
    total_locations: totalLocations,
    found_locations: foundLocations,
    client_mentions: clientMentions,
    mentions_by_platform: platformMentions,
    top_sources: sources,
    llm_sources: llmSources,
    narrative_tags: tags,
    competitors,
    authority,
  } = insights;

  const clientName = domain ? domain.split('.')[0] : 'Client';
  const displayName = clientName.charAt(0).toUpperCase() + clientName.slice(1);

  return (
    <>
      {/* ── AI Insights ──────────────────────────────────────────────── */}
      <Typography className={classes.sectionHeading}>AI Insights</Typography>
      <Typography className={classes.sectionDesc}>
        This overview highlights your overall visibility, presence, and positioning across AI search
        platforms, giving you a clear baseline for comparison and growth.
      </Typography>

      <Paper className={classes.card}>
        <Box display="flex" flexWrap="wrap" style={{ gap: 0 }}>
          {/* Col 1 — AI Visibility Score */}
          <Box flex={1} minWidth={200} pr={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Typography className={classes.colTitle}>
              AI Visibility Score
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 16 }}>
              <PercentileRing percentile={percentile} />
              <Typography variant="body2" style={{ fontSize: 12, color: '#666', maxWidth: 180 }}>
                Ranked higher than {100 - percentile}% of firms appearing in your tracked AI queries.
              </Typography>
            </Box>
          </Box>

          {/* Col 2 — Mentions */}
          <Box flex={1} minWidth={180} px={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Typography className={classes.colTitle}>Mentions</Typography>
            {PLATFORMS.map((p) => {
              const count = platformMentions?.[p.key];
              return (
                <div key={p.key} className={classes.platformRow}>
                  <Box display="flex" alignItems="center">
                    <div className={classes.platformIcon} style={{ background: p.bg }}>
                      {p.letter}
                    </div>
                    <span style={{ fontSize: 13, color: '#333' }}>{p.name}</span>
                  </Box>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      fontSize: 13,
                      color: count != null ? '#333' : '#bbb',
                    }}
                  >
                    {count != null ? formatCount(count) : 'Coming Soon'}
                  </span>
                </div>
              );
            })}
          </Box>

          {/* Col 3 — Sources */}
          <Box flex={1} minWidth={180} px={3} style={{ borderRight: '1px solid #f0f0f0' }}>
            <Typography className={classes.colTitle}>Sources (ChatGPT)</Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8, fontSize: 10 }}>
              Top sites cited alongside you in AI responses
            </Typography>
            {(llmSources && llmSources.length > 0 ? llmSources : sources).slice(0, 8).map((s) => (
              <div key={s.domain} className={classes.sourceRow}>
                <span style={{ fontSize: 13, color: '#333' }}>
                  {s.domain?.length > 22 ? `${s.domain.slice(0, 22)}...` : s.domain}
                </span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#333' }}>
                  {formatCount(s.mentions)}
                </span>
              </div>
            ))}
          </Box>

          {/* Col 4 — AI Narrative */}
          <Box flex={1} minWidth={200} pl={3}>
            <Typography className={classes.colTitle}>AI Narrative</Typography>
            <Box mb={2}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  variant="outlined"
                  className={classes.tag}
                  style={{
                    borderColor: '#0d9373',
                    color: '#0d9373',
                  }}
                />
              ))}
            </Box>
            <Divider style={{ margin: '8px 0' }} />
            <Typography style={{ fontSize: 12, fontWeight: 700, color: '#58595B', marginBottom: 10 }}>
              Client Journey Visibility
            </Typography>
            {/* Informational bar */}
            <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
              <Box
                flex={1}
                style={{ background: '#e5e7eb', borderRadius: 4, height: 22, overflow: 'hidden', position: 'relative' }}
              >
                <div
                  style={{
                    width: `${insights.client_journey?.informational || 0}%`,
                    height: '100%',
                    background: '#0d9373',
                    borderRadius: 4,
                    transition: 'width 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 8,
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                    Informational Queries
                  </span>
                </div>
              </Box>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, minWidth: 32, textAlign: 'right' }}>
                {insights.client_journey?.informational || 0}%
              </span>
            </Box>
            {/* Transactional bar */}
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Box
                flex={1}
                style={{ background: '#e5e7eb', borderRadius: 4, height: 22, overflow: 'hidden', position: 'relative' }}
              >
                <div
                  style={{
                    width: `${insights.client_journey?.transactional || 0}%`,
                    height: '100%',
                    background: '#1e3a5f',
                    borderRadius: 4,
                    transition: 'width 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 8,
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                    Transactional Queries
                  </span>
                </div>
              </Box>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, minWidth: 32, textAlign: 'right' }}>
                {insights.client_journey?.transactional || 0}%
              </span>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Domain Authority ────────────────────────────────────────── */}
      {authority && (authority.total_backlinks > 0 || authority.referring_domains > 0) && (
        <>
          <Typography className={classes.sectionHeading} style={{ marginTop: 8 }}>
            Domain Authority
          </Typography>
          <Typography className={classes.sectionDesc}>
            Backlink profile and authority signals that influence how AI models rank and recommend your firm.
          </Typography>

          <Paper className={classes.card}>
            <Box display="flex" flexWrap="wrap" style={{ gap: 24 }}>
              {/* Client metrics */}
              <Box flex={1} minWidth={200}>
                <Typography style={{ fontSize: 13, fontWeight: 700, color: '#0d9373', marginBottom: 12 }}>
                  {displayName}
                </Typography>
                {authority.domain_rating > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" style={{ fontSize: 13, color: '#666' }}>
                      Domain Rating
                    </Typography>
                    <Typography style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#0d9373' }}>
                      {authority.domain_rating}
                    </Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" style={{ fontSize: 13, color: '#666' }}>
                    Backlinks
                  </Typography>
                  <Typography style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#0d9373' }}>
                    {formatCount(authority.total_backlinks)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" style={{ fontSize: 13, color: '#666' }}>
                    Referring Domains
                  </Typography>
                  <Typography style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#0d9373' }}>
                    {formatCount(authority.referring_domains)}
                  </Typography>
                </Box>
              </Box>

              {/* Competitor comparison bars */}
              {authority.competitors && authority.competitors.length > 0 && (
                <Box flex={2} minWidth={300}>
                  <Typography style={{ fontSize: 12, fontWeight: 700, color: '#58595B', marginBottom: 12 }}>
                    Referring Domains Comparison
                  </Typography>
                  {[
                    { name: domain, value: authority.referring_domains, isClient: true },
                    ...authority.competitors.map((c) => ({
                      name: c.domain,
                      value: c.referring_domains,
                      isClient: false,
                    })),
                  ].map((item) => {
                    const maxVal = Math.max(
                      authority.referring_domains,
                      ...authority.competitors.map((c) => c.referring_domains),
                    );
                    const pct = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
                    return (
                      <Box key={item.name} display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
                        <Typography
                          variant="body2"
                          style={{
                            width: 160,
                            fontSize: 12,
                            fontWeight: item.isClient ? 700 : 400,
                            color: item.isClient ? '#0d9373' : '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Box flex={1} style={{ background: '#e5e7eb', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background: item.isClient ? '#0d9373' : '#94a3b8',
                              borderRadius: 4,
                              transition: 'width 0.3s',
                            }}
                          />
                        </Box>
                        <Typography
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            fontSize: 12,
                            minWidth: 50,
                            textAlign: 'right',
                            color: item.isClient ? '#0d9373' : '#333',
                          }}
                        >
                          {formatCount(item.value)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* ── Competitive Visibility Comparison ────────────────────────── */}
      {competitors.length > 0 && (
        <>
          <Typography className={classes.sectionHeading} style={{ marginTop: 8 }}>
            Competitive Visibility Comparison
          </Typography>
          <Typography className={classes.sectionDesc}>
            Compare your brand&apos;s visibility in AI search alongside key competitors to see how your
            mentions and reach align within your market.
          </Typography>

          <Paper className={classes.card}>
            {/* Header row */}
            <div className={classes.compRow} style={{ borderBottom: '2px solid #e5e7eb' }}>
              <div className={classes.compLabel} />
              <div className={classes.compCell}>
                <span className={classes.compHeader} style={{ color: '#0d9373' }}>
                  {displayName}
                </span>
              </div>
              {competitors.map((c) => (
                <div key={c.domain} className={classes.compCell}>
                  <span className={classes.compHeader}>{c.firm_name}</span>
                </div>
              ))}
            </div>

            {/* Gauge row */}
            <div className={classes.compRow} style={{ minHeight: 120, padding: '16px 0' }}>
              <div className={classes.compLabel}>AI Visibility Score</div>
              <div className={classes.compCell}>
                <SemiGauge score={visScore} />
              </div>
              {competitors.map((c) => (
                <div key={c.domain} className={classes.compCell}>
                  <SemiGauge score={c.visibility_score} />
                </div>
              ))}
            </div>

            {/* Mentions row — clickable */}
            <div
              className={classes.compRow}
              onClick={() => setMentionsModal(true)}
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setMentionsModal(true); }}
            >
              <div className={classes.compLabel}>
                Mentions
                <Typography variant="caption" style={{ display: 'block', color: '#999', fontSize: 10 }}>
                  Click for details
                </Typography>
              </div>
              <div className={classes.compCell}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#0d9373' }}>
                  {formatCount(clientMentions)}
                </span>
              </div>
              {competitors.map((c) => (
                <div key={c.domain} className={classes.compCell}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#333' }}>
                    {formatCount(c.mentions)}
                  </span>
                </div>
              ))}
            </div>

            {/* Locations row — clickable */}
            <div
              className={classes.compRow}
              style={{ borderBottom: 'none', cursor: 'pointer' }}
              onClick={() => setLocationsModal(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setLocationsModal(true); }}
            >
              <div className={classes.compLabel}>
                Locations Visible
                <Typography variant="caption" style={{ display: 'block', color: '#999', fontSize: 10 }}>
                  Click for details
                </Typography>
              </div>
              <div className={classes.compCell}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#0d9373' }}>
                  {foundLocations}/{totalLocations}
                </span>
              </div>
              {competitors.map((c) => (
                <div key={c.domain} className={classes.compCell}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 18, color: '#333' }}>
                    {c.found_locations}/{totalLocations}
                  </span>
                </div>
              ))}
            </div>
          </Paper>
        </>
      )}

      {/* Recommendations */}
      <AiRecommendations recommendations={insights.recommendations} />

      {/* Mentions Detail Modal */}
      <Dialog open={mentionsModal} onClose={() => setMentionsModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Mentions Breakdown</span>
            <IconButton size="small" onClick={() => setMentionsModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            How many times each firm appears across all grid search results (practice area × city × template combinations).
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Firm</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Mentions</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Avg Rank</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow style={{ background: '#f0fdf4' }}>
                <TableCell style={{ fontWeight: 700, color: '#0d9373' }}>{displayName} ({domain})</TableCell>
                <TableCell align="right" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{clientMentions}</TableCell>
                <TableCell align="right" style={{ fontFamily: 'monospace' }}>—</TableCell>
              </TableRow>
              {competitors.map((c) => (
                <TableRow key={c.domain}>
                  <TableCell>
                    {c.firm_name}
                    <Typography variant="caption" style={{ display: 'block', color: '#999', fontSize: 10 }}>{c.domain}</Typography>
                  </TableCell>
                  <TableCell align="right" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.mentions}</TableCell>
                  <TableCell align="right" style={{ fontFamily: 'monospace' }}>
                    #{Math.round(110 - c.visibility_score) / 10 || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Locations Detail Modal */}
      <Dialog open={locationsModal} onClose={() => setLocationsModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Locations Visibility</span>
            <IconButton size="small" onClick={() => setLocationsModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Which locations each firm appears in across all practice areas (best rank shown).
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Firm</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="center">Locations</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="center">Coverage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow style={{ background: '#f0fdf4' }}>
                <TableCell style={{ fontWeight: 700, color: '#0d9373' }}>{displayName}</TableCell>
                <TableCell align="center" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {foundLocations}/{totalLocations}
                </TableCell>
                <TableCell align="center">
                  <Box style={{ background: '#e5e7eb', borderRadius: 4, height: 16, overflow: 'hidden', width: 80, display: 'inline-block' }}>
                    <div style={{ width: `${totalLocations > 0 ? (foundLocations / totalLocations) * 100 : 0}%`, height: '100%', background: '#0d9373', borderRadius: 4 }} />
                  </Box>
                </TableCell>
              </TableRow>
              {competitors.map((c) => (
                <TableRow key={c.domain}>
                  <TableCell>
                    {c.firm_name}
                    <Typography variant="caption" style={{ display: 'block', color: '#999', fontSize: 10 }}>{c.domain}</Typography>
                  </TableCell>
                  <TableCell align="center" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {c.found_locations}/{totalLocations}
                  </TableCell>
                  <TableCell align="center">
                    <Box style={{ background: '#e5e7eb', borderRadius: 4, height: 16, overflow: 'hidden', width: 80, display: 'inline-block' }}>
                      <div style={{ width: `${totalLocations > 0 ? (c.found_locations / totalLocations) * 100 : 0}%`, height: '100%', background: '#94a3b8', borderRadius: 4 }} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </>
  );
};

AiInsights.propTypes = {
  loading: PropTypes.bool,
  insights: PropTypes.shape({
    visibility_score: PropTypes.number,
    top_percentile: PropTypes.number,
    total_locations: PropTypes.number,
    found_locations: PropTypes.number,
    client_mentions: PropTypes.number,
    mentions_by_platform: PropTypes.shape({
      chatgpt: PropTypes.number,
      google_ai_overview: PropTypes.number,
      gemini: PropTypes.number,
      perplexity: PropTypes.number,
      claude: PropTypes.number,
    }),
    top_sources: PropTypes.arrayOf(PropTypes.shape({
      domain: PropTypes.string,
      mentions: PropTypes.number,
      firm_name: PropTypes.string,
    })),
    llm_sources: PropTypes.arrayOf(PropTypes.shape({
      domain: PropTypes.string,
      mentions: PropTypes.number,
    })),
    client_journey: PropTypes.shape({
      informational: PropTypes.number,
      transactional: PropTypes.number,
    }),
    narrative_tags: PropTypes.arrayOf(PropTypes.string),
    competitors: PropTypes.arrayOf(PropTypes.shape({
      domain: PropTypes.string,
      firm_name: PropTypes.string,
      visibility_score: PropTypes.number,
      mentions: PropTypes.number,
      found_locations: PropTypes.number,
    })),
    authority: PropTypes.shape({
      domain_rating: PropTypes.number,
      ahrefs_rank: PropTypes.number,
      total_backlinks: PropTypes.number,
      referring_domains: PropTypes.number,
      competitors: PropTypes.arrayOf(PropTypes.shape({
        domain: PropTypes.string,
        firm_name: PropTypes.string,
        domain_rating: PropTypes.number,
        referring_domains: PropTypes.number,
        total_backlinks: PropTypes.number,
      })),
    }),
    recommendations: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string,
      priority: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      action_verb: PropTypes.string,
    })),
  }),
};

AiInsights.defaultProps = {
  insights: null,
  loading: false,
};

export default AiInsights;
